<template>
  <div class="customersSatisfactionReport">
    <Loading v-if="loading" />
    <div class="customersSatisfactionReport__chooseDate">
      <div class="customersSatisfactionReport__chooseDate__container">
        <div class="chooseDate-date-inputs">
          <div class="input">
            <Calendar
              :key="calendarStartKey"
              type="date"
              format="DD/MM/YYYY"
              name="startTime"
              :value="firstDay"
              :message="$t('errorDate')"
              @change="changeDateStartTime"
            />
          </div>
          <img class="date-img" src="@/assets/icons/Hyphen.svg" alt="Hyphen" />
          <div class="input">
            <Calendar
              :key="calendarEndKey"
              type="date"
              format="DD/MM/YYYY"
              name="endTime"
              :value="today"
              @change="changeDateEndTime"
            />
          </div>
        </div>
        <Dropdown
          class="customersSatisfactionReport__thisPeriod"
          :selected-option="getOptionThisPeriod.selectedOption"
          :options="getOptionThisPeriod.options"
          @select-option="selectOptionThisPeriod"
        />
      </div>
      <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
    </div>
    <div class="customersSatisfactionReport__diagrams">
      <div
        v-if="
          dataLineChart &&
          dataLineChart.labels.length &&
          (!dataLineChart.datasets[0].data.every((value) => !value) ||
            !dataLineChart.datasets[1].data.every((value) => !value))
        "
        :class="{
          'line-diagram': true,
          'line-diagram-long': dataPieChart.datasets[0].data.every(
            (value) => !value
          )
        }"
      >
        <LineChart
          :id="'lineChartTwoLegends'"
          :data-line-chart="dataLineChart"
        />
        <div class="satisfaction-periods">
          <div class="thisPeriod">
            <p>{{ $t('thisPeriod') }}</p>
          </div>
          <div class="otherPeriod">
            <Dropdown
              class="customersSatisfactionReport__otherPeriod"
              :selected-option="getOptionOtherPeriod.selectedOption"
              :options="getOptionOtherPeriod.options"
              @select-option="selectOptionOtherPeriod"
            />
          </div>
        </div>
      </div>
      <div
        v-if="
          dataPieChart &&
          dataPieChart.labels.length &&
          !loading &&
          !dataPieChart.datasets[0].data.every((value) => !value)
        "
        class="pie-diagram"
      >
        <PieChart
          :data-pie-chart="dataPieChart"
          :style="stylePieChart"
          @segment-click="handleSegmentClick"
        />
      </div>
    </div>
    <Description
      :data="dailyStatistics?.currentPharmacyStats.textData"
      :pickup-boxes="entityNames?.pickupBoxes"
      :options="pharmacy?.feedbackTemplate.options_screen.options"
    />
    <Amount :data="dailyStatistics?.currentPharmacyStats.amountData" />
    <div class="container">
      <div
        v-if="
          dataBarChart?.labels?.length &&
          (!dataBarChart.datasets[0].data.every((value) => !value) ||
            !dataBarChart.datasets[1].data.every((value) => !value) ||
            !dataBarChart.datasets[2].data.every((value) => !value))
        "
        class="customersSatisfactionReport__barChart"
      >
        <BarChart
          :id="'barChart'"
          :data-bar-chart="dataBarChart"
          :options-bar-chart="optionsBarChart"
          :style="styleBarChart"
        />
      </div>
    </div>
    <div class="container">
      <div class="customersSatisfactionReport__generalSatisfaction">
        <HeatmapChart :heatmap-data="filteredData" />
      </div>
    </div>
    <div class="container">
      <div
        v-if="
          dataBarChart?.labels?.length &&
          (!dataBarChart.datasets[0].data.every((value) => !value) ||
            !dataBarChart.datasets[1].data.every((value) => !value) ||
            !dataBarChart.datasets[2].data.every((value) => !value))
        "
        class="customersSatisfactionReport__barChart"
      >
        <BarChart
          :id="'barChartAmount'"
          :data-bar-chart="dataBarChartAmount"
          :options-bar-chart="optionsBarChartAmount"
          :style="styleBarChart"
        />
      </div>
    </div>

    <CustomersFeedbackBlock
      v-if="showBlockCustomersFeedbackBlock"
      :data="dailyStatistics?.currentPharmacyStats.comments"
    />
  </div>

  <AllFeedback
    :feedback="feedback"
    :selected-key="selectedKey"
    :comment-key="commentKey"
    :show-modal-all-feedback="showModalAllFeedback"
    @close-modal="showModalAllFeedback = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import { format } from 'date-fns';

import Loading from '@/views/Loading.vue';

import AllFeedback from './AllFeedback/AllFeedback.vue';
import Description from './Description/Description.vue';
import Amount from './Amount/Amount.vue';
import CustomersFeedbackBlock from './CustomersFeedbackBlock/CustomersFeedbackBlock.vue';

import router from '@/router';

import {
  activeOptions,
  activeOtherPeriodOptions,
  colorScale,
  getLabelsMap,
  defaultDataLineChart,
  defaultDataPieChart,
  defaultDataBarChart,
  defaultDataBarChartAmount,
  optionsBarChart,
  optionsBarChartAmount
} from './configs';

import { InputFileEvent, Option } from '@/types';
import { LineChartDataset } from '@/store/modules/pharmacy/types';

@Options({
  name: 'CustomersSatisfactionReport',
  props: ['pharmacy'],
  components: {
    Loading,
    AllFeedback,
    Description,
    Amount,
    CustomersFeedbackBlock
  },
  data() {
    return {
      today: format(
        new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
          23,
          59,
          59,
          999
        ),
        'dd/MM/yyyy HH:mm:ss'
      ),
      firstDay: format(
        new Date(
          new Date(new Date().setDate(new Date().getDate() - 7)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        'dd/MM/yyyy HH:mm:ss'
      ),
      calendarStartKey: 0,
      calendarEndKey: 0,
      height: 340,
      heightPieChart: 300,
      heightBarChart: 360,
      widthBarChart: 1330,
      filteredData: [],
      feedback: null,
      selectedKey: '',
      commentKey: '',
      colorScale,
      getLabelsMap,
      loading: false,
      changeOptions: false,
      showModalAllFeedback: false,
      dataLineChart: defaultDataLineChart,
      dataPieChart: defaultDataPieChart,
      dataBarChart: defaultDataBarChart,
      dataBarChartAmount: defaultDataBarChartAmount,
      optionsBarChart: optionsBarChart,
      optionsBarChartAmount: optionsBarChartAmount,
      selectedThisPeriod: 'week',
      selectedOtherPeriod: 'prevPeriod',
      params: {
        pharmacyId: '',
        startTime: new Date(
          new Date().setDate(new Date().getDate() - 7)
        ).setHours(0, 0, 0, 0),
        endTime: new Date(
          new Date().setDate(new Date().getDate() - 1)
        ).setHours(23, 59, 59, 999),
        thisPeriod: 'week',
        otherPeriod: 'prevPeriod'
      },
      getOptionThisPeriod: {
        selectedOption: {
          value: 'week',
          label: this.$t('week')
        },
        options: activeOptions(this.$t)
      },
      getOptionOtherPeriod: {
        selectedOption: {
          value: 'prevPeriod',
          label: this.$t('prevPeriod')
        },
        options: activeOtherPeriodOptions(this.$t)
      }
    };
  },
  computed: {
    ...mapState('orders', ['dailyStatistics']),
    ...mapState('entityNames', ['entityNames']),
    isDateCorrect() {
      if (this.isDatePresent) {
        return this.params.endTime >= this.params.startTime;
      }

      return true;
    },
    isDatePresent() {
      return this.params && this.params.startTime && this.params.endTime;
    },
    showBlockCustomersFeedbackBlock() {
      const comments = this.dailyStatistics?.currentPharmacyStats.comments;

      if (!comments) return false;

      const hasAnyComment = Object.values(comments).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      );

      return hasAnyComment;
    },
    stylePieChart() {
      return {
        height: `${this.heightPieChart}px`
      };
    },
    styleBarChart() {
      return {
        height: `${this.heightBarChart}px`,
        width: `${this.widthBarChart}px`
      };
    }
  },
  methods: {
    ...mapActions('orders', ['getCustomersSatisfaction']),
    ...mapActions('entityNames', ['getEntityNames']),
    async show() {
      await this.getEntityNames();
    },
    async initData() {
      this.loading = true;

      const defaultParams = { ...this.params };

      if (
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        await this.getCustomersSatisfaction(defaultParams);

        this.show();

        this.initCustomersSatisfactionData();
        this.initPieChartData();
        this.initGroupedChartData();
        this.initBarChartAmount();
        this.initFilteredData();

        this.loading = false;
      }
    },
    selectOptionThisPeriod(option: Option) {
      this.getOptionThisPeriod.selectedOption = option;
    },
    selectOptionOtherPeriod(option: Option) {
      this.getOptionOtherPeriod.selectedOption = option;

      this.selectedOtherPeriod = this.getOptionOtherPeriod.selectedOption.value;
    },
    handleSegmentClick({ key }: { key: string }) {
      this.loading = true;

      this.commentKey = key;
      this.selectedKey = this.getLabelsMap()[key];
      this.feedback =
        this.dailyStatistics.currentPharmacyStats.comments[key] || [];

      this.loading = false;
      this.showModalAllFeedback = true;
    },
    getLabels() {
      const labelsLineChart = Object.keys(
        this.dailyStatistics.currentPharmacyStats.lineGraphicsData.thisPeriod
      ).map((date: string) => date);

      return labelsLineChart;
    },
    initCustomersSatisfactionData() {
      const stats = this.dailyStatistics.currentPharmacyStats.lineGraphicsData;

      const dataThisPeriod = Object.values(stats.thisPeriod);
      const dataOtherPeriod = Object.values(stats.otherPeriod);

      this.dataLineChart = {
        ...this.dataLineChart,
        labels: this.getLabels(),
        datasets: this.dataLineChart.datasets.map((item: LineChartDataset) => {
          switch (item.label) {
            case 'thisPeriod':
              return { ...item, data: dataThisPeriod };
            case 'otherPeriod':
              return { ...item, data: dataOtherPeriod };
            default:
              return item;
          }
        })
      };
    },
    initPieChartData() {
      const totals =
        this.dailyStatistics.currentPharmacyStats.pieChartData || {};

      const order = Object.keys(this.getLabelsMap());

      const data = order.map((key) => {
        const v = totals[key];

        return v;
      });

      this.dataPieChart = {
        labels: order.map((key) => this.getLabelsMap()[key]),
        datasets: [
          {
            ...this.dataPieChart.datasets[0],
            data
          }
        ]
      };
    },
    initGroupedChartData() {
      if (
        this.dailyStatistics.currentPharmacyStats.lineGraphicsData.thisPeriod
      ) {
        const stats =
          this.dailyStatistics.currentPharmacyStats.groupedChartData;

        this.dataBarChart = {
          ...stats,
          datasets: [
            {
              ...this.dataBarChart.datasets[0],
              label: this.$t('angry'),
              data: Object.values(stats.datasets[0].data)
            },
            {
              ...this.dataBarChart.datasets[1],
              label: this.$t('ok'),
              data: Object.values(stats.datasets[1].data)
            },
            {
              ...this.dataBarChart.datasets[2],
              label: this.$t('happy'),
              data: Object.values(stats.datasets[2].data)
            }
          ],
          labels: this.getLabels()
        };
      }
    },
    initBarChartAmount() {
      if (
        this.dailyStatistics.currentPharmacyStats.lineGraphicsData.thisPeriod
      ) {
        const getColorByPercent = (percent: number): string => {
          const step = Math.round(percent / 10);
          return this.colorScale[Math.min(step, this.colorScale.length - 1)];
        };

        const stats =
          this.dailyStatistics.currentPharmacyStats.barChartSatisfactionData
            .datasets;

        this.dataBarChartAmount = {
          labels: this.getLabels(),
          datasets: [
            {
              ...this.dataBarChartAmount.datasets,
              data: stats.data,
              backgroundColor: stats.satisfactionPercent.map(
                (percent: number) => getColorByPercent(percent)
              )
            }
          ]
        };
      }
    },
    initFilteredData() {
      this.filteredData =
        this.dailyStatistics.currentPharmacyStats &&
        this.dailyStatistics.currentPharmacyStats.satisfactionRaw
          ? this.dailyStatistics.currentPharmacyStats.satisfactionRaw
          : [];
    },
    changeDateStartTime(e: InputFileEvent) {
      const [day, month, year] = e.target.value.split('/');

      const dateObj = new Date(`${month}/${day}/${year}`);
      dateObj.setHours(0, 0, 0, 0);

      const date = dateObj.getTime();

      this.firstDay = format(dateObj, 'dd/MM/yyyy');
      this.calendarStartKey++;

      const now = new Date();
      const defaultWeekStart = new Date().setDate(now.getDate() - 7);
      const defaultMonthStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      ).getTime();

      if (this.getOptionThisPeriod.selectedOption) {
        if (
          (this.getOptionThisPeriod.selectedOption.value === 'week' &&
            date !== defaultWeekStart) ||
          (this.getOptionThisPeriod.selectedOption.value === 'month' &&
            date !== defaultMonthStart)
        ) {
          this.getOptionThisPeriod.selectedOption = { value: '', label: '' };
        }
      }

      this.params = {
        ...this.params,
        startTime: dateObj.valueOf()
      };
    },
    changeDateEndTime(e: InputFileEvent) {
      const [day, month, year] = e.target.value.split('/');

      const dateObj = new Date(`${month}/${day}/${year}`);
      dateObj.setHours(23, 59, 59, 999);

      const date = dateObj.getTime();

      this.today = format(dateObj, 'dd/MM/yyyy');
      this.calendarEndKey++;

      const now = new Date();
      const defaultEnd = now.getTime();

      if (this.getOptionThisPeriod.selectedOption && date !== defaultEnd) {
        this.getOptionThisPeriod.selectedOption = { value: '', label: '' };
      }

      this.params = {
        ...this.params,
        endTime: dateObj.valueOf()
      };
    },
    closeTimePanel() {
      this.initChartsData();
    },
    goToSettings() {
      router.push({ name: 'SettingsCustomersFeedback' });
    }
  },
  watch: {
    params: {
      deep: true,
      handler() {
        if (this.isDateCorrect) {
          clearTimeout(this._paramsTimeout);

          this.dataLineChart = { ...defaultDataLineChart };
          this.dataPieChart = { ...defaultDataPieChart };
          this.dataBarChart = { ...defaultDataBarChart };
          this.dataBarChartAmount = { ...defaultDataBarChartAmount };

          this._paramsTimeout = setTimeout(() => {
            this.initData(this.params);
          }, 0);
        }
      }
    },
    selectedThisPeriod() {
      this.params = {
        ...this.params,
        thisPeriod: this.selectedThisPeriod
      };
    },
    selectedOtherPeriod() {
      this.params = {
        ...this.params,
        otherPeriod: this.selectedOtherPeriod
      };
    },
    'getOptionThisPeriod.selectedOption'(option: Option) {
      if (!option) return;

      const now = new Date();
      const end = new Date(
        new Date().setDate(new Date().getDate() - 1)
      ).setHours(23, 59, 59, 999);

      if (option.value === 'week') {
        const firstOfWeek = new Date(
          new Date().setDate(new Date().getDate() - 7)
        ).setHours(0, 0, 0, 0);

        this.params = {
          ...this.params,
          startTime: firstOfWeek,
          endTime: end
        };

        this.firstDay = format(
          new Date().setDate(now.getDate() - 7),
          'dd/MM/yyyy'
        );

        this.today = format(end, 'dd/MM/yyyy');
      }

      if (option.value === 'month') {
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        this.params = {
          ...this.params,
          startTime: firstOfMonth.getTime(),
          endTime: end
        };

        this.firstDay = format(firstOfMonth, 'dd/MM/yyyy');
        this.today = format(end, 'dd/MM/yyyy');
      }

      this.calendarStartKey++;
      this.calendarEndKey++;

      this.selectedThisPeriod = option.value;
    },
    pharmacy: {
      handler(newVal) {
        if (!newVal) return;

        this.params = {
          ...this.params,
          pharmacyId: newVal.id
        };
      },
      immediate: true
    }
  }
})
export default class CustomersSatisfactionReport extends Vue {}
</script>

<style lang="scss" scoped>
.customersSatisfactionReport {
  .customersSatisfactionReport__chooseDate {
    margin: 32px 24px;
    .customersSatisfactionReport__chooseDate__container {
      display: flex;
      align-content: center;
      justify-content: flex-start;
      .chooseDate-date-inputs {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin-right: 24px;
        .formkit-outer {
          height: 42px;
          margin-bottom: 0 !important;
          .formkit-wrapper {
            height: 42px;
            .formkit-inner {
              height: 42px;
            }
          }
        }
        .input {
          position: relative;
          margin-bottom: 0;
          z-index: 2;
          .input-img {
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 0;
          }
        }
        .date-img {
          margin: 0 8px;
        }
      }
      .customersSatisfactionReport__thisPeriod {
        width: 150px;
      }
    }
    .date-error {
      color: $red;
      font-size: $fourteenSize;
      margin-top: 10px;
    }
  }
  .customersSatisfactionReport__diagrams {
    padding: 0 24px;
    display: flex;
    justify-content: center;
    @media (max-width: 1440px) {
      justify-content: flex-start;
    }
    .line-diagram {
      position: relative;
      width: 723px;
      height: 350px;
      padding: 20px;
      padding-left: 46px;
      border: 1px solid $border;
      border-radius: 8px;
      margin-right: 20px;
      @media (max-width: 1280px) {
        width: 593px;
        height: 300px;
      }
      &.line-diagram-long {
        max-width: 1099px;
        width: 100%;
        padding-left: 60px;
        margin-right: 0;
        @media (max-width: 1280px) {
          max-width: 941px;
        }
      }
    }
    .pie-diagram {
      width: 330px;
      height: 350px;
      padding: 20px;
      border: 1px solid $border;
      border-radius: 8px;
      @media (max-width: 1280px) {
        width: 307px;
        height: 300px;
      }
    }
    .satisfaction-periods {
      position: absolute;
      top: 14%;
      right: 55px;
      transform: translate(0, -50%);
      width: 330px;
      height: 31px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .thisPeriod {
        position: relative;
        p {
          display: inline-block;
          width: max-content;
          padding: 8px;
          border: 1px solid $border;
          border-radius: 8px;
          color: $lineColor;
          font-size: 14px;
        }
        &::before {
          position: absolute;
          content: '';
          top: 50%;
          right: 85%;
          transform: translate(-50%, -50%);
          width: 50px;
          height: 2px;
          background-color: $lineColor;
        }
      }
      .otherPeriod {
        height: 31px;
        .customersSatisfactionReport__otherPeriod {
          height: 30px;
          width: 160px;
          .dropdown__head {
            height: 30px;
          }
        }
        &::before {
          position: absolute;
          content: '- - - - - -';
          top: 50%;
          right: 44%;
          transform: translate(-50%, -50%);
          width: 50px;
          color: $dottedlineColor;
        }
      }
    }
  }
  .container {
    display: flex;
    justify-content: center;
    @media (max-width: 1440px) {
      display: block;
    }
    @media (max-width: 1366px) {
      max-width: 1068px;
    }
    @media (max-width: 1280px) {
      max-width: 981px;
    }
    .customersSatisfactionReport__barChart {
      max-width: 1140px;
      width: 100%;
      border: 1px solid $border;
      border-radius: 8px;
      padding: 20px;
      margin: 24px;
    }
  }
  .container {
    .customersSatisfactionReport__generalSatisfaction {
      max-width: 1140px;
      width: 100%;
      border: 1px solid $border;
      border-radius: 8px;
      padding: 20px;
      margin: 24px;
    }
  }
}
</style>
